import React, { useEffect, useRef } from 'react';
import { Map, View } from 'ol';
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer';
import { XYZ, Vector as VectorSource } from 'ol/source';
import { fromLonLat } from 'ol/proj';
import { defaults as defaultControls } from 'ol/control';
import { defaults as defaultInteractions } from 'ol/interaction';
import { Style, Stroke, Fill } from 'ol/style';
import { Polygon } from 'ol/geom';
import { Feature } from 'ol';
import 'ol/ol.css';

interface InsetMapProps {
  mainMap?: Map;
  className?: string;
}

export default function InsetMap({ mainMap, className = '' }: InsetMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const insetMapRef = useRef<Map | null>(null);
  const extentLayerRef = useRef<VectorLayer | null>(null);

  useEffect(() => {
    if (!mapRef.current) return;

    const mainCenter = mainMap?.getView().getCenter() || fromLonLat([124.659593, 8.067894]);
    const mainZoom = mainMap?.getView().getZoom() || 10;

    const insetMap = new Map({
      target: mapRef.current,
      layers: [
        new TileLayer({
          source: new XYZ({
            url: 'https://api.maptiler.com/maps/streets-v2/{z}/{x}/{y}@2x.png?key=Otbh9YhFMbwux7HyoffB',
            crossOrigin: 'anonymous',
            tileSize: 512,
          }),
        })
      ],
      view: new View({
        center: mainCenter,
        zoom: Math.max(mainZoom - 5, 1),
      }),
      controls: defaultControls({
        zoom: false,
        attribution: false,
        rotate: false
      }),
      interactions: defaultInteractions({
        doubleClickZoom: false,
        dragPan: false,
        mouseWheelZoom: false,
        keyboard: false,
        pinchZoom: false,
        pinchRotate: false,
      }),
    });

    const vectorSource = new VectorSource();
    const extentLayer = new VectorLayer({
      source: vectorSource,
      style: new Style({
        stroke: new Stroke({
          color: '#2563eb',
          width: 2.5,
          lineDash: [6, 4],
        }),
        fill: new Fill({
          color: 'rgba(37, 99, 235, 0.12)',
        }),
      }),
      zIndex: 1000,
    });

    insetMap.addLayer(extentLayer);
    insetMapRef.current = insetMap;
    extentLayerRef.current = extentLayer;

    const updateExtent = () => {
      if (!mainMap || !extentLayerRef.current || !insetMapRef.current) return;

      const mainView = mainMap.getView();
      const extent = mainView.calculateExtent();
      const size = mainMap.getSize();
      const zoom = mainView.getZoom();
      const center = mainView.getCenter();

      if (extent && size && zoom && center) {
        vectorSource.clear();

        const extentCoords = [
          [extent[0], extent[1]],
          [extent[2], extent[1]],
          [extent[2], extent[3]],
          [extent[0], extent[3]],
          [extent[0], extent[1]]
        ];

        const extentFeature = new Feature({
          geometry: new Polygon([extentCoords]),
        });
        extentFeature.set('type', 'main_map_bbox');
        vectorSource.addFeature(extentFeature);

        const insetView = insetMapRef.current.getView();
        const insetZoom = Math.max(zoom - 5, 1);
        insetView.setCenter(center);
        insetView.setZoom(insetZoom);
      }
    };

    const handleInsetMapClick = (event: any) => {
      if (!mainMap) return;
      const clickedCoordinate = event.coordinate;
      const mainView = mainMap.getView();
      const currentZoom = mainView.getZoom() || 10;

      mainView.animate({
        center: clickedCoordinate,
        zoom: currentZoom,
        duration: 500
      });
    };

    insetMap.on('singleclick', handleInsetMapClick);

    let isDragging = false;
    let dragStartCoordinate: any = null;

    const handlePointerDown = (event: any) => {
      const feature = insetMap.forEachFeatureAtPixel(event.pixel, (f) => f);
      if (feature) {
        isDragging = true;
        dragStartCoordinate = event.coordinate;
        (insetMap.getTarget() as HTMLElement).style.cursor = 'grabbing';
        event.preventDefault();
      }
    };

    const handlePointerMove = (event: any) => {
      if (isDragging && dragStartCoordinate && mainMap) {
        const dragCurrentCoordinate = event.coordinate;
        const deltaX = dragCurrentCoordinate[0] - dragStartCoordinate[0];
        const deltaY = dragCurrentCoordinate[1] - dragStartCoordinate[1];

        const mainView = mainMap.getView();
        const currentCenter = mainView.getCenter();
        if (currentCenter) {
          mainView.setCenter([currentCenter[0] + deltaX, currentCenter[1] + deltaY]);
        }
        dragStartCoordinate = dragCurrentCoordinate;
      } else {
        const feature = insetMap.forEachFeatureAtPixel(event.pixel, (f) => f);
        (insetMap.getTarget() as HTMLElement).style.cursor = feature ? 'grab' : 'pointer';
      }
    };

    const handlePointerUp = () => {
      if (isDragging) {
        isDragging = false;
        dragStartCoordinate = null;
        (insetMap.getTarget() as HTMLElement).style.cursor = 'pointer';
      }
    };

    insetMap.on('pointerdown', handlePointerDown);
    insetMap.on('pointermove', handlePointerMove);
    insetMap.on('pointerup', handlePointerUp);

    if (mainMap) {
      const mainView = mainMap.getView();
      mainView.on('change:center', updateExtent);
      mainView.on('change:resolution', updateExtent);
      mainMap.on('moveend', updateExtent);
      mainMap.on('postrender', updateExtent);
      updateExtent();
    }

    return () => {
      if (insetMapRef.current) {
        insetMapRef.current.un('singleclick', handleInsetMapClick);
        insetMapRef.current.un('pointerdown', handlePointerDown);
        insetMapRef.current.un('pointermove', handlePointerMove);
        insetMapRef.current.un('pointerup', handlePointerUp);

        if (mainMap) {
          const mainView = mainMap.getView();
          mainView.un('change:center', updateExtent);
          mainView.un('change:resolution', updateExtent);
          mainMap.un('moveend', updateExtent);
          mainMap.un('postrender', updateExtent);
        }

        insetMapRef.current.setTarget(undefined);
        insetMapRef.current = null;
      }
    };
  }, [mainMap]);

  return (
    <div className={`z-10 ${className}`}>
      <div className="bg-white rounded-lg shadow-xl border border-gray-300 overflow-hidden">
        <div className="relative">
          <div
            ref={mapRef}
            className="bg-gray-100"
            style={{ width: '200px', height: '140px' }}
          />

          {/* Viewport indicator legend */}
          <div className="absolute bottom-1.5 left-1.5 bg-black/70 text-white text-[9px] px-1.5 py-0.5 rounded flex items-center gap-1">
            <div className="w-3 h-1.5 border border-blue-400 border-dashed bg-blue-400/20 rounded-sm"></div>
            <span>Viewport</span>
          </div>
        </div>
      </div>
    </div>
  );
}
